/**
 * Descendant Grouping Utilities
 * 
 * Functions for analyzing shared descendants between nodes.
 * Used by crossing minimization to group related nodes together.
 */

/**
 * Get all descendants of a node (children, grandchildren, etc.)
 * @param {string} nodeId - Starting node
 * @param {Object} graph - Dependency graph
 * @returns {Set} Set of all descendant node IDs
 */
export function getAllDescendants(nodeId, graph) {
  const descendants = new Set();
  const queue = [nodeId];
  
  while (queue.length > 0) {
    const current = queue.shift();
    const children = graph.children.get(current) || [];
    for (const child of children) {
      if (!descendants.has(child)) {
        descendants.add(child);
        queue.push(child);
      }
    }
  }
  
  return descendants;
}

/**
 * Find shared descendants between two nodes
 * @param {string} nodeA - First node
 * @param {string} nodeB - Second node
 * @param {Object} graph - Dependency graph
 * @returns {Set} Set of shared descendant IDs
 */
export function getSharedDescendants(nodeA, nodeB, graph) {
  const descendantsA = getAllDescendants(nodeA, graph);
  const descendantsB = getAllDescendants(nodeB, graph);
  
  const shared = new Set();
  for (const id of descendantsA) {
    if (descendantsB.has(id)) {
      shared.add(id);
    }
  }
  return shared;
}

/**
 * Calculate connectivity score between two nodes based on shared descendants.
 * Higher score = more closely related = should be placed adjacent.
 * 
 * Scoring factors:
 * - Number of shared descendants (more = higher score)
 * - Depth of first shared descendant (shallower = higher score, more urgent to group)
 * 
 * @param {string} nodeA - First node
 * @param {string} nodeB - Second node  
 * @param {Object} graph - Dependency graph
 * @param {Map} taskLayers - Map of nodeId -> layer index
 * @returns {number} Connectivity score (0 = no connection)
 */
export function calculateConnectivityScore(nodeA, nodeB, graph, taskLayers) {
  const shared = getSharedDescendants(nodeA, nodeB, graph);
  if (shared.size === 0) return 0;
  
  // Find the shallowest (closest) shared descendant
  let minDepth = Infinity;
  const nodeALayer = taskLayers.get(nodeA) || 0;
  
  for (const descendantId of shared) {
    const descendantLayer = taskLayers.get(descendantId) || 0;
    const depth = descendantLayer - nodeALayer;
    if (depth < minDepth) {
      minDepth = depth;
    }
  }
  
  // Score: prioritize by closeness (inverse of depth), then by count
  // Closer merges are more important to group (they create more visible crossings)
  const depthScore = minDepth > 0 ? 100 / minDepth : 100;
  const countScore = Math.min(shared.size, 10); // Cap count contribution
  
  return depthScore + countScore;
}

/**
 * Group nodes in a layer by their shared descendants using Union-Find.
 * Nodes that share descendants should be in the same group.
 * 
 * @param {Array} layer - Array of node IDs in this layer
 * @param {Object} graph - Dependency graph
 * @param {Map} taskLayers - Map of nodeId -> layer index
 * @returns {Array} Array of groups, each group is an array of node IDs
 */
export function groupBySharedDescendants(layer, graph, taskLayers) {
  if (layer.length <= 1) return [layer];
  
  // Union-Find data structure
  const parent = new Map();
  const rank = new Map();
  
  // Initialize: each node is its own parent
  layer.forEach(nodeId => {
    parent.set(nodeId, nodeId);
    rank.set(nodeId, 0);
  });
  
  // Find with path compression
  function find(x) {
    if (parent.get(x) !== x) {
      parent.set(x, find(parent.get(x)));
    }
    return parent.get(x);
  }
  
  // Union by rank
  function union(x, y) {
    const rootX = find(x);
    const rootY = find(y);
    if (rootX === rootY) return;
    
    const rankX = rank.get(rootX);
    const rankY = rank.get(rootY);
    
    if (rankX < rankY) {
      parent.set(rootX, rootY);
    } else if (rankX > rankY) {
      parent.set(rootY, rootX);
    } else {
      parent.set(rootY, rootX);
      rank.set(rootX, rankX + 1);
    }
  }
  
  // Connect nodes that share descendants
  for (let i = 0; i < layer.length; i++) {
    for (let j = i + 1; j < layer.length; j++) {
      const score = calculateConnectivityScore(layer[i], layer[j], graph, taskLayers);
      if (score > 0) {
        union(layer[i], layer[j]);
      }
    }
  }
  
  // Collect groups
  const groups = new Map();
  layer.forEach(nodeId => {
    const root = find(nodeId);
    if (!groups.has(root)) {
      groups.set(root, []);
    }
    groups.get(root).push(nodeId);
  });
  
  return Array.from(groups.values());
}

/**
 * Order groups by their "center of gravity" - where their descendants are positioned.
 * Groups whose descendants are more to the left should be placed left.
 * 
 * @param {Array} groups - Array of node groups
 * @param {Object} graph - Dependency graph
 * @param {Array} nextLayer - The layer below (children layer)
 * @param {Map} taskLayers - Map of nodeId -> layer index
 * @param {number} currentLayerIndex - Index of the current layer
 * @returns {Array} Ordered array of groups
 */
export function orderGroupsByDescendantPosition(groups, graph, nextLayer, taskLayers, currentLayerIndex) {
  if (groups.length <= 1) return groups;
  if (!nextLayer || nextLayer.length === 0) return groups;
  
  // Create position map for next layer
  const nextLayerPositions = new Map();
  nextLayer.forEach((nodeId, index) => nextLayerPositions.set(nodeId, index));
  
  // Calculate average descendant position for each group
  const groupScores = groups.map(group => {
    let totalPosition = 0;
    let count = 0;
    
    // Look at immediate children in the next layer
    for (const nodeId of group) {
      const children = graph.children.get(nodeId) || [];
      for (const childId of children) {
        if (nextLayerPositions.has(childId)) {
          totalPosition += nextLayerPositions.get(childId);
          count++;
        }
      }
    }
    
    // Also consider all descendants for groups with no immediate children in next layer
    if (count === 0) {
      for (const nodeId of group) {
        const descendants = getAllDescendants(nodeId, graph);
        for (const descId of descendants) {
          if (nextLayerPositions.has(descId)) {
            totalPosition += nextLayerPositions.get(descId);
            count++;
          }
        }
      }
    }
    
    const avgPosition = count > 0 ? totalPosition / count : Infinity;
    
    // For determinism, use the smallest node ID in the group as tiebreaker
    const minId = group.reduce((min, id) => id < min ? id : min, group[0]);
    
    return { group, avgPosition, minId, hasChildren: count > 0 };
  });
  
  // Sort: groups with children first, then by average position, then by ID
  groupScores.sort((a, b) => {
    // Groups with children come before groups without
    if (a.hasChildren && !b.hasChildren) return -1;
    if (!a.hasChildren && b.hasChildren) return 1;
    
    // Sort by average position
    const posDiff = a.avgPosition - b.avgPosition;
    if (Math.abs(posDiff) > 0.001) return posDiff;
    
    // Tie-break by smallest ID in group
    return (a.minId || '').localeCompare(b.minId || '');
  });
  
  return groupScores.map(s => s.group);
}
